import { Box, CircularProgress, Grid, TextField, Tooltip } from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { Field } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import contextStore from "stores/contextStore";

const styles = {
  warningText: {
    input: {
      WebkitTextFillColor: "red !important",
      color: "red !important",
    },
  },
  clickableZone: {
    position: "relative",
    cursor: "pointer",
    height: "50px",
    width: "100%",
    mt: "-50px",
  },
};

export enum ContextOrganizationFieldMode {
  SetByHeader = "SetByHeader",
  EditDisabled = "EditDisabled",
  EditManuallyTriggered = "EditManuallyTriggered",
}

type ContextOrganizationFieldProps<T> = {
  values: T;
  label?: string;
  mode?: ContextOrganizationFieldMode;
  organizationFieldName?: keyof T & string;
  formChange: <F extends keyof T>(name: F, value?: T[F]) => void;
};

const ContextOrganizationField = <T extends {}>({
  values,
  label,
  mode: modeProp,
  organizationFieldName = "organizationId" as any,
  formChange,
}: ContextOrganizationFieldProps<T>) => {
  const { formatMessage: t } = useIntl();
  const { organizationId } = contextStore.useState();
  const { organizationService } = useGlobalContext();
  const [mode, setMode] = useState(
    modeProp ?? ContextOrganizationFieldMode.SetByHeader
  );

  const { data: organization, isLoading } = useQuery(
    ["organization", values[organizationFieldName]],
    () =>
      organizationService.getOrganization(
        values[organizationFieldName] as string
      ),
    {
      enabled: !!values[organizationFieldName],
    }
  );

  useEffect(() => {
    mode === ContextOrganizationFieldMode.SetByHeader &&
      formChange(organizationFieldName, organizationId as any);
  }, [organizationId, formChange, mode, organizationFieldName]);

  const handleClick = () => {
    mode === ContextOrganizationFieldMode.EditManuallyTriggered &&
      setMode(ContextOrganizationFieldMode.SetByHeader);
  };

  const tooltipText = t({
    id:
      mode === ContextOrganizationFieldMode.EditDisabled
        ? "changingOrganizationIsDisabled"
        : mode === ContextOrganizationFieldMode.SetByHeader
        ? "organizationInheritedFromTheHeaderFilter"
        : "toChangeTheOrganizationClickIntoTheField",
  });

  const modeChanged =
    modeProp === ContextOrganizationFieldMode.EditManuallyTriggered &&
    mode === ContextOrganizationFieldMode.SetByHeader;

  return (
    <>
      <Field name={organizationFieldName} required>
        {({ meta }) => (
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              required
              value={
                isLoading || !values[organizationFieldName]
                  ? ""
                  : organization?.data?.name
              }
              variant="standard"
              label={t({ id: label ?? "organization" })}
              error={meta.error}
              helperText={meta.error}
              InputProps={{
                required: true,
                startAdornment: isLoading ? (
                  <CircularProgress size={16} />
                ) : null,
                sx: modeChanged || meta.error ? styles.warningText : undefined,
              }}
            />
          </Grid>
        )}
      </Field>
      <Tooltip title={tooltipText}>
        <Box onClick={handleClick} sx={styles.clickableZone}></Box>
      </Tooltip>
    </>
  );
};

export default ContextOrganizationField;
